import {
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {t} from 'i18next';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReactComponent as DropDownIcon} from '../../../../Aurora/src/assests/svg/down_arrow.svg';
import {ReactComponent as DropUpIcon} from '../../../../Aurora/src/assests/svg/up_arrow.svg';
import AurIcon, {IconNames, IconColors} from '../common/aur-icon/AurIcon';
import {AurTextField} from '../common/aur-text-field/AurTextField';
import {setSearchValues} from '../../redux/slices/bulkUserFileNameSlice';
import {RootState} from '../../redux/store';
import styles from './style.module.scss';
import {useFieldFocusStyle} from '../../hooks/utils/useFieldFocusStyle';

const CustomMenu = styled(Menu)(() => ({
  '& .MuiPaper-root': {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '16px',
    },
    '&::-webkit-scrollbar-track': {
      marginTop: '2px',
      marginBottom: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#546A8C',
      borderRadius: '100px',
      border: '6px solid transparent',
      backgroundClip: 'content-box',
    },
  },
}));
interface ClientListDropdownProps {
  buttonOrTextField: string;
  data?: Array<Record<string, any>>; // Accepts any object type
  displayKey: string; // Key to display in dropdown
  idKey: string; // Key to use as the identifier
  buttonLabel?: string;
  dropDownID: string;
  dropDownEntity: any;
  setDropDownEntity: any;
  showSearchBox: boolean;
}

const ClientListDropdown = (props: ClientListDropdownProps) => {
  const {
    buttonOrTextField = 'button',
    data,
    displayKey,
    idKey,
    buttonLabel,
    dropDownID,
    dropDownEntity,
    setDropDownEntity,
    showSearchBox = false,
  } = props;
  const dispatch = useDispatch();
  
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchTermData = useSelector(
    (state: RootState) => state.bulkUserFile.searchTerm
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const {borderClass, focusHandler, mouseKeyDownHandler} = useFieldFocusStyle({
    tabFocusClass: styles.tabFocusedBorder,
    mouseFocusClass: styles.mouseFocusedBorder,
  });

  const open = Boolean(anchorEl);

  const showSearchBoxCheck = showSearchBox ? 11 : 12;

  const searchBoxVisible: boolean =
    (buttonOrTextField === 'button' &&
      showSearchBox &&
      searchTermData !== '') ||
    (dropDownEntity?.options.length ?? 0) > showSearchBoxCheck;
    
  const arrowIcon = open ? <DropUpIcon aria-hidden="true" /> : <DropDownIcon aria-hidden="true" />;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      dispatch(setSearchValues(searchTerm));
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [dispatch, searchTerm]);

  useEffect(() => {
    const formattedData = data?.map((item) => ({
      id: item[idKey],
      display: item[displayKey],
    }));

    setDropDownEntity({...dropDownEntity, options: formattedData});
  }, [
    dispatch,
    dropDownID,
    data,
    displayKey,
    idKey,
    setDropDownEntity,
    dropDownEntity,
  ]);

  const filteredDataList = dropDownEntity?.options
    ?.filter((item: any) =>
      item.display?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    )
    .sort((a: {display: string}, b: {display: any}) =>
      a.display.localeCompare(b.display)
    );

  const handleSelect = (item: any) => {
    const find = dropDownEntity?.options?.find((ele: any) => ele.id == item.id);
    setDropDownEntity({...dropDownEntity, selectedOption: find.id});
    if (!showSearchBox) {
      setSearchTerm('');
    }
    setAnchorEl(null);
  };

  const handleToggleDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  return (
    <div className="aur-client-dropdown">
      <Button
        variant="outlined"
        onClick={handleToggleDropdown}
        endIcon={arrowIcon}
        className={`client-dropdown-button ${borderClass}`}
        sx={{textTransform: 'none'}}
        onFocus={focusHandler}
        onMouseDown={mouseKeyDownHandler}
      >
        <div className="button-text-div">
          <Typography className={`typo-text label2_size custom-label`}>
            {buttonLabel}
          </Typography>
          <Typography
            className={`typo-text subhead3_size custom-button-text ${buttonOrTextField === 'text' ? 'mist-100' : ''}`}
          >
            {
              dropDownEntity?.options.find(
                (opt: any) => opt.id === dropDownEntity?.selectedOption
              )?.display
            }
          </Typography>
        </div>
      </Button>
      <CustomMenu
        className="aurClientDropDownMenu"
        aria-labelledby="addUserButton"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        transformOrigin={{vertical: 'top', horizontal: 'center'}}
        disablePortal
        role="menu"
        disableScrollLock
      >
        {searchBoxVisible && (
          <AurTextField
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="filled"
            className="aur-textfield custom-text-field client-dropDown-search-box"
            onKeyDown={(e) => e.stopPropagation()}
            label={t('userHomePage.search')}
            ariaLabel={t('userHomePage.search')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AurIcon
                    icon={IconNames.Search}
                    customClassName="client-drop-down-search-icon"
                    color={IconColors.Custom}
                  />
                </InputAdornment>
              ),
            }}
          />
        )}
        {filteredDataList?.map((item: any) => (
          <MenuItem
            key={item.id}
            onClick={() => handleSelect(item)}
            tabIndex={0}
            className={`aurClientDropDownMenuItems ${dropDownEntity?.selectedOption === item.id ? 'selected' : ''}`}
          >
            <Typography className="typo-text body2_size menu-items-text">
              {item.display}
            </Typography>
            {dropDownEntity?.selectedOption === item.id && (
              <img
                src="/assests/images/icon_checkmark.svg"
                className="check-icon"
              />
            )}
          </MenuItem>
        ))}
      </CustomMenu>
    </div>
  );
};

export default ClientListDropdown;
